/**
 * Display formatting helpers for manual ingestion job history.
 */

/**
 * Format an ISO timestamp in the browser's local timezone.
 *
 * @param value - ISO-8601 timestamp
 * @returns Local date/time label or an em dash when absent
 */
export function formatJobTimestamp(value?: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

/**
 * Format elapsed time between job start and completion.
 *
 * @param startedAt - ISO-8601 start timestamp
 * @param completedAt - ISO-8601 completion timestamp
 * @returns Compact elapsed duration or an em dash when unavailable
 */
export function formatJobDuration(
  startedAt?: string | null,
  completedAt?: string | null,
): string {
  if (!startedAt || !completedAt) return "—";
  const elapsed =
    new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return "—";
  const seconds = Math.floor(elapsed / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours) return `${hours}h ${minutes % 60}m`;
  if (minutes) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/**
 * Convert a persisted source code into a UI label.
 *
 * @param source - Job source code
 * @returns Human-readable source label
 */
export function formatJobSource(source?: string | null): string {
  if (source === "csv") return "CSV upload";
  if (source === "mrns") return "MRN list";
  return "—";
}

/**
 * Convert known infrastructure errors into actionable operator guidance.
 *
 * @param error - Persisted worker error
 * @returns Human-readable error message or an em dash when absent
 */
export function formatJobError(error?: string | null): string {
  if (!error) return "—";
  const normalized = error.toLowerCase();
  if (
    normalized.includes("flatiron-token mint failed") ||
    normalized.includes("flatiron fhir did not respond")
  ) {
    return "Flatiron FHIR did not respond in time. Resume the job to try again.";
  }
  if (
    normalized.includes("readtimeout") ||
    normalized.includes("timeouterror")
  ) {
    return "A required service did not respond in time. Resume the job to try again.";
  }
  if (normalized.includes("batch checkpoints are still leased")) {
    return "A previous worker still owns this batch. Resume the job when the current lease is released.";
  }
  return error;
}
